import type { useTranslation } from "@/core/i18n";
import { formatCurrency } from "toolbox/Money";

import type { EnabledLoyaltyStatus } from "./useLoyaltyStatus";

type Translate = ReturnType<typeof useTranslation>["t"];

/** "You've reached Gold (for life)" for a patient on the top tier; undefined for anyone else. */
export const topTierCopy = (t: Translate, status: EnabledLoyaltyStatus) => {
  const tier = status.currentTier;
  if (!tier || !status.isTopTier) return undefined;
  if (status.rules.topTierIsLifetime) return t("loyalty.reachedTierForLife", { tier: tier.name });
  return t("loyalty.reachedTier", { tier: tier.name });
};

/**
 * "2 more visits to Gold", or "2 more visits or $400 in the app to Gold" when spend in the app
 * counts toward status. Undefined on the top tier, which has nothing left to earn.
 */
export const nextTierCopy = (t: Translate, status: EnabledLoyaltyStatus) => {
  const { nextTier, visitsToNextTier, spendToNextTierInCents } = status;
  if (!nextTier) return undefined;
  if (typeof visitsToNextTier !== "number") {
    throw new Error("Loyalty status has a next tier but no visits to it");
  }
  if (typeof spendToNextTierInCents === "number") {
    return t("loyalty.moreVisitsOrSpendInAppToTier", {
      count: visitsToNextTier,
      amount: formatCurrency(spendToNextTierInCents, 0),
      tier: nextTier.name,
    });
  }
  return t("loyalty.moreVisitsToTier", { count: visitsToNextTier, tier: nextTier.name });
};

/** The status in one line: the top-tier copy, else what is left to the next tier. */
export const statusLine = (t: Translate, status: EnabledLoyaltyStatus) =>
  topTierCopy(t, status) ?? nextTierCopy(t, status);
